import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";

import React, {
  useContext,
  useEffect,
  useRef,
  useState
} from "react";

import {
  Avatar,
  IconButton,
  InputBase,
  makeStyles,
  Paper,
  Typography
} from "@material-ui/core";

import SendRoundedIcon from "@material-ui/icons/SendRounded";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import AttachFileRoundedIcon from "@material-ui/icons/AttachFileRounded";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import GetAppRoundedIcon from "@material-ui/icons/GetAppRounded";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import MicRoundedIcon from "@material-ui/icons/MicRounded";
import MoodRoundedIcon from "@material-ui/icons/MoodRounded";
import MicRecorder from "mic-recorder-to-mp3";
import ReplyIcon from "@material-ui/icons/Reply";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles(theme => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    background:
      theme.palette.type === "dark"
        ? "#07111F"
        : "#F8FAFC",
    borderLeft:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,.07)"
        : "1px solid #E2E8F0"
  },

  header: {
    minHeight: 64,
    flexShrink: 0,
    padding: "9px 16px",
    display: "flex",
    alignItems: "center",
    gap: 11,
    background:
      theme.palette.type === "dark"
        ? "#0B1627"
        : "#FFFFFF",
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,.07)"
        : "1px solid #E2E8F0"
  },

  headerAvatar: {
    width: 40,
    height: 40,
    fontSize: 14,
    fontWeight: 800,
    background: "linear-gradient(135deg,#0057FF,#00A3FF)"
  },

  headerInfo: {
    minWidth: 0
  },

  headerTitle: {
    fontSize: 13.5,
    lineHeight: 1.25,
    fontWeight: 900,
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A"
  },

  headerSubtitle: {
    marginTop: 3,
    maxWidth: 520,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 11,
    color:
      theme.palette.type === "dark"
        ? "#64748B"
        : "#94A3B8"
  },

  messageList: {
    flex: 1,
    overflowY: "auto",
    padding: "18px 18px 10px",
    ...theme.scrollbarStyles
  },

  messageRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginBottom: 12
  },

  messageRowMine: {
    justifyContent: "flex-end"
  },

  messageAvatar: {
    width: 28,
    height: 28,
    fontSize: 10,
    fontWeight: 800,
    flexShrink: 0,
    background: "#E2E8F0",
    color: "#475569"
  },

  bubble: {
    position: "relative",
    maxWidth: "min(620px, 72%)",
    padding: "9px 11px 7px",
    borderRadius: "14px 14px 14px 4px",
    background:
      theme.palette.type === "dark"
        ? "#111C2D"
        : "#FFFFFF",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,.06)"
        : "1px solid #E2E8F0",
    boxShadow:
      theme.palette.type === "dark"
        ? "none"
        : "0 2px 7px rgba(15,23,42,.04)"
  },

  bubbleMine: {
    borderRadius: "14px 14px 4px 14px",
    background:
      theme.palette.type === "dark"
        ? "#0B3472"
        : "#EAF2FF",
    borderColor:
      theme.palette.type === "dark"
        ? "rgba(59,130,246,.28)"
        : "#D6E7FF"
  },

  sender: {
    display: "block",
    marginBottom: 3,
    fontSize: 10.5,
    fontWeight: 900,
    color: "#0057FF"
  },

  messageText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 13,
    lineHeight: 1.42,
    textAlign: "left",
    color:
      theme.palette.type === "dark"
        ? "#E2E8F0"
        : "#172033"
  },

  time: {
    display: "block",
    marginTop: 5,
    textAlign: "right",
    fontSize: 9.5,
    color:
      theme.palette.type === "dark"
        ? "#64748B"
        : "#94A3B8"
  },

  inputArea: {
    position: "relative",
    flexShrink: 0,
    padding: "10px 13px 12px",
    background:
      theme.palette.type === "dark"
        ? "#0B1627"
        : "#FFFFFF",
    borderTop:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,.07)"
        : "1px solid #E2E8F0"
  },

  composer: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    padding: "5px 6px 5px 14px",
    borderRadius: 18,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.05)"
        : "#F8FAFC",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,.08)"
        : "1px solid #E2E8F0",

    "&:focus-within": {
      borderColor: "#93C5FD",
      boxShadow: "0 0 0 3px rgba(59,130,246,.08)"
    }
  },

  input: {
    flex: 1,
    minHeight: 36,
    maxHeight: 110,
    padding: "7px 0",
    fontSize: 13
  },

  attachmentPreview: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    marginBottom: 8,
    padding: "8px 10px",
    borderRadius: 12,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.05)"
        : "#F8FAFC",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,.08)"
        : "1px solid #E2E8F0"
  },

  attachmentName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 11.5,
    fontWeight: 700
  },

  attachButton: {
    width: 36,
    height: 36,
    color: "#64748B"
  },

  fileCard: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    minWidth: 220,
    maxWidth: 380,
    marginTop: 6,
    padding: "8px 9px",
    borderRadius: 11,
    background: "rgba(255,255,255,.62)",
    border: "1px solid rgba(148,163,184,.28)"
  },

  fileIcon: {
    width: 34,
    height: 34,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    background: "#EFF6FF",
    color: "#0057FF"
  },

  fileInfo: {
    flex: 1,
    minWidth: 0
  },

  fileName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 11.5,
    fontWeight: 800
  },

  fileType: {
    marginTop: 2,
    fontSize: 9.5,
    textTransform: "uppercase",
    color: "#94A3B8"
  },

  fileDownload: {
    color: "#0057FF"
  },

  replyButton: {
    position: "absolute",
    top: -11,
    right: 7,
    width: 26,
    height: 26,
    padding: 0,
    zIndex: 2,
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#64748B",
    background:
      theme.palette.type === "dark"
        ? "#172235"
        : "#FFFFFF",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,.10)"
        : "1px solid #E2E8F0",
    boxShadow: "0 2px 7px rgba(15,23,42,.10)",

    "&:hover": {
      color: "#0057FF",
      background:
        theme.palette.type === "dark"
          ? "#1E293B"
          : "#F8FAFC"
    }
  },

  quotedMessage: {
    marginBottom: 7,
    padding: "7px 9px",
    borderRadius: 9,
    cursor: "pointer",
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.055)"
        : "rgba(15,23,42,.035)",
    borderLeft: "3px solid #0057FF"
  },

  quotedAuthor: {
    display: "block",
    marginBottom: 2,
    fontSize: 10.5,
    fontWeight: 900,
    color: "#0057FF"
  },

  quotedText: {
    maxWidth: 430,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 11.5,
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475569"
  },

  replyPreview: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    marginBottom: 8,
    padding: "8px 9px 8px 11px",
    borderRadius: 11,
    background:
      theme.palette.type === "dark"
        ? "rgba(0,87,255,.10)"
        : "#F3F7FF",
    borderLeft: "3px solid #0057FF"
  },

  replyPreviewContent: {
    flex: 1,
    minWidth: 0
  },

  replyPreviewAuthor: {
    display: "block",
    marginBottom: 2,
    fontSize: 10.5,
    fontWeight: 900,
    color: "#0057FF"
  },

  replyPreviewText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 11.5,
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475569"
  },

  audioPlayerCard: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 260,
    maxWidth: 390,
    marginTop: 6,
    padding: "7px 8px",
    borderRadius: 12,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.05)"
        : "rgba(255,255,255,.70)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,.08)"
        : "1px solid rgba(148,163,184,.25)"
  },

  audioElement: {
    flex: 1,
    width: 220,
    maxWidth: "100%",
    height: 34
  },

  audioSpeedButton: {
    minWidth: 42,
    height: 30,
    padding: "0 8px",
    border: "1px solid #CBD5E1",
    borderRadius: 15,
    background: "#FFFFFF",
    color: "#334155",
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer",

    "&:hover": {
      background: "#F1F5F9"
    }
  },

  audioLoading: {
    marginTop: 6,
    fontSize: 11,
    color: "#64748B"
  },

  emojiButton: {
    width: 36,
    height: 36,
    color: "#64748B"
  },

  emojiPickerWrap: {
    position: "absolute",
    left: 14,
    bottom: 66,
    zIndex: 1500,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 14px 42px rgba(15,23,42,.18)",

    "& .emoji-mart": {
      border: "1px solid #E2E8F0",
      borderRadius: 14
    },

    [theme.breakpoints.down("sm")]: {
      left: 8,
      right: 8,
      bottom: 68,

      "& .emoji-mart": {
        width: "100% !important"
      }
    }
  },

  micButton: {
    width: 36,
    height: 36,
    color: "#0057FF"
  },

  recordingComposer: {
    width: "100%",
    minHeight: 46,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "5px 7px"
  },

  recordingCancel: {
    width: 36,
    height: 36,
    color: "#EF4444"
  },

  recordingStatus: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0
  },

  recordingDot: {
    width: 9,
    height: 9,
    flexShrink: 0,
    borderRadius: "50%",
    background: "#EF4444",
    boxShadow: "0 0 0 4px rgba(239,68,68,.10)"
  },

  recordingLabel: {
    fontSize: 12,
    fontWeight: 800,
    color:
      theme.palette.type === "dark"
        ? "#E2E8F0"
        : "#334155"
  },

  recordingTimer: {
    marginLeft: "auto",
    fontVariantNumeric: "tabular-nums",
    fontSize: 12,
    fontWeight: 900,
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475569"
  },

  recordingSend: {
    width: 36,
    height: 36,
    background: "#0057FF",
    color: "#FFFFFF",

    "&:hover": {
      background: "#0046CC"
    }
  },

  sendButton: {
    width: 36,
    height: 36,
    marginBottom: 1,
    background: "#0057FF",
    color: "#FFFFFF",

    "&:hover": {
      background: "#0046CC"
    },

    "&.Mui-disabled": {
      background: "#E2E8F0",
      color: "#94A3B8"
    }
  }
}));

const initials = name =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join("")
    .toUpperCase();

const formatDateTime = value => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const day = date.toLocaleDateString(
    "pt-BR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    }
  );

  const time = date.toLocaleTimeString(
    "pt-BR",
    {
      hour: "2-digit",
      minute: "2-digit"
    }
  );

  return `${day} • ${time}`;
};

const isAudioFile = name =>
  String(name || "")
    .toLowerCase()
    .endsWith(".mp3");

const formatRecordingTime = totalSeconds => {
  const minutes =
    Math.floor(totalSeconds / 60);

  const seconds =
    totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(
    seconds
  ).padStart(2, "0")}`;
};

function InternalChatAudio({
  chatId,
  item,
  classes
}) {
  const audioRef = useRef();
  const [url, setUrl] = useState("");
  const [speed, setSpeed] = useState(1);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let mounted = true;
    let objectUrl = "";

    const load = async () => {
      try {
        const response = await api.get(
          `/chats/${chatId}/files/${item.id}`,
          {
            responseType: "blob"
          }
        );

        if (!mounted) return;

        const blob = new Blob(
          [response.data],
          {
            type: "audio/mpeg"
          }
        );

        objectUrl =
          window.URL.createObjectURL(blob);

        setUrl(objectUrl);
      } catch (err) {
        if (mounted) {
          setFailed(true);
        }
      }
    };

    load();

    return () => {
      mounted = false;

      if (objectUrl) {
        window.URL.revokeObjectURL(
          objectUrl
        );
      }
    };
  }, [chatId, item.id]);

  const changeSpeed = () => {
    const next =
      speed === 1
        ? 1.5
        : speed === 1.5
          ? 2
          : 1;

    setSpeed(next);

    if (audioRef.current) {
      audioRef.current.playbackRate =
        next;
    }
  };

  if (failed) {
    return (
      <div className={classes.audioLoading}>
        Não foi possível carregar o áudio.
      </div>
    );
  }

  if (!url) {
    return (
      <div className={classes.audioLoading}>
        Carregando áudio...
      </div>
    );
  }

  return (
    <div className={classes.audioPlayerCard}>
      <audio
        ref={audioRef}
        controls
        preload="metadata"
        src={url}
        className={classes.audioElement}
        onPlay={event => {
          event.currentTarget.playbackRate =
            speed;
        }}
      />

      <button
        type="button"
        className={classes.audioSpeedButton}
        onClick={changeSpeed}
        title="Velocidade do áudio"
      >
        {speed}x
      </button>
    </div>
  );
}

export default function ChatMessages({
  chat,
  messages,
  handleSendMessage,
  handleLoadMore,
  scrollToBottomRef,
  pageInfo,
  loading
}) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const baseRef = useRef();
  const listRef = useRef();
  const fileInputRef = useRef();
  const messageInputRef = useRef();

  const lastRenderedMessageIdRef =
    useRef(null);

  const activeChatIdRef =
    useRef(null);

  const recorderRef =
    useRef(null);

  if (!recorderRef.current) {
    recorderRef.current =
      new MicRecorder({
        bitRate: 128
      });
  }

  const backendUrl = getBackendUrl();

  const [contentMessage, setContentMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);

  const [emojiOpen, setEmojiOpen] =
    useState(false);

  const [recording, setRecording] =
    useState(false);

  const [
    recordingSeconds,
    setRecordingSeconds
  ] = useState(0);

  const participants = (chat?.users || [])
    .map(item => item.user)
    .filter(Boolean);

  const otherParticipant =
    participants.find(
      participant => Number(participant.id) !== Number(user.id)
    ) || participants[0];

  const chatTitle =
    String(chat?.title || "").trim() ||
    otherParticipant?.name ||
    "Chat interno";

  const participantNames = participants
    .filter(
      participant => Number(participant.id) !== Number(user.id)
    )
    .map(participant => participant.name)
    .filter(Boolean);

  const subtitle =
    participantNames.length > 0
      ? participantNames.join(", ")
      : "Equipe";

  const headerAvatar =
    otherParticipant?.profileImage
      ? `${backendUrl}/public/avatar/${otherParticipant.profileImage}`
      : undefined;

  const scrollToBottom = () => {
    if (baseRef.current) {
      baseRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end"
      });
    }
  };

  useEffect(() => {
    setReplyingTo(null);
    setEmojiOpen(false);

    if (!chat?.id) return;

    const currentUser = (chat.users || []).find(
      item => Number(item.userId) === Number(user.id)
    );

    if (Number(currentUser?.unreads || 0) > 0) {
      api
        .post(`/chats/${chat.id}/read`, {
          userId: user.id
        })
        .catch(() => {});
    }

    scrollToBottomRef.current = scrollToBottom;

    activeChatIdRef.current =
      Number(chat.id);

    lastRenderedMessageIdRef.current =
      null;
  }, [chat?.id, user.id]);

  useEffect(() => {
    if (
      !Array.isArray(messages) ||
      messages.length === 0 ||
      !chat?.id
    ) {
      return;
    }

    const lastMessage =
      messages[messages.length - 1];

    const lastId =
      Number(lastMessage?.id || 0);

    if (!lastId) return;

    const chatChanged =
      Number(activeChatIdRef.current) !==
      Number(chat.id);

    const lastChanged =
      Number(
        lastRenderedMessageIdRef.current ||
        0
      ) !== lastId;

    if (
      chatChanged ||
      lastChanged
    ) {
      activeChatIdRef.current =
        Number(chat.id);

      lastRenderedMessageIdRef.current =
        lastId;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          scrollToBottom();
        });
      });
    }
  }, [chat?.id, messages]);

  useEffect(() => {
    if (!recording) return undefined;

    const timer =
      window.setInterval(() => {
        setRecordingSeconds(
          previous =>
            previous + 1
        );
      }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [recording]);

  const handleScroll = event => {
    const { scrollTop } = event.currentTarget;

    if (!pageInfo?.hasMore || loading) return;

    if (scrollTop < 500) {
      handleLoadMore();
    }
  };

  const messagePreview = item => {
    const text =
      String(item?.message || "").trim();

    if (text) return text;

    if (item?.mediaName) {
      return `📎 ${item.mediaName}`;
    }

    return "Mensagem";
  };

  const startReply = item => {
    setReplyingTo(item);

    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 0);
  };

  const scrollToQuotedMessage = messageId => {
    if (!messageId || !listRef.current) return;

    const element =
      listRef.current.querySelector(
        `[data-chat-message-id="${messageId}"]`
      );

    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });

    if (element.animate) {
      element.animate(
        [
          { opacity: 0.55 },
          { opacity: 1 }
        ],
        {
          duration: 650,
          easing: "ease-out"
        }
      );
    }
  };

  const send = () => {
    const value = contentMessage.trim();

    setEmojiOpen(false);

    if (
      (!value && !attachment) ||
      loading
    ) {
      return;
    }

    handleSendMessage(
      value,
      attachment,
      replyingTo?.id || null
    );

    setContentMessage("");
    setAttachment(null);
    setReplyingTo(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSelectEmoji = emoji => {
    const nativeEmoji =
      emoji?.native || "";

    if (!nativeEmoji) return;

    const input =
      messageInputRef.current;

    const current =
      String(contentMessage || "");

    if (
      input &&
      typeof input.selectionStart === "number"
    ) {
      const start =
        input.selectionStart;

      const end =
        typeof input.selectionEnd === "number"
          ? input.selectionEnd
          : start;

      const next =
        current.slice(0, start) +
        nativeEmoji +
        current.slice(end);

      setContentMessage(next);

      setTimeout(() => {
        input.focus();

        const cursor =
          start + nativeEmoji.length;

        if (
          typeof input.setSelectionRange ===
          "function"
        ) {
          input.setSelectionRange(
            cursor,
            cursor
          );
        }
      }, 0);
    } else {
      setContentMessage(
        current + nativeEmoji
      );

      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 0);
    }
  };

  const handleStartRecording = async () => {
    if (
      loading ||
      recording ||
      attachment ||
      contentMessage.trim()
    ) {
      return;
    }

    try {
      setEmojiOpen(false);

      await recorderRef.current.start();

      setRecordingSeconds(0);
      setRecording(true);
    } catch (err) {
      console.error(
        "Erro ao iniciar gravação:",
        err
      );

      window.alert(
        "Não foi possível acessar o microfone. Verifique a permissão do navegador."
      );
    }
  };

  const handleCancelRecording = async () => {
    try {
      if (recording) {
        await recorderRef.current
          .stop()
          .getMp3();
      }
    } catch (_) {}

    setRecording(false);
    setRecordingSeconds(0);
  };

  const handleSendRecording = async () => {
    if (
      !recording ||
      loading
    ) {
      return;
    }

    try {
      const [buffer] =
        await recorderRef.current
          .stop()
          .getMp3();

      const audioFile =
        new File(
          buffer,
          `audio-${Date.now()}.mp3`,
          {
            type: "audio/mpeg",
            lastModified: Date.now()
          }
        );

      setRecording(false);
      setRecordingSeconds(0);

      await handleSendMessage(
        "",
        audioFile,
        replyingTo?.id || null
      );

      setReplyingTo(null);
    } catch (err) {
      console.error(
        "Erro ao enviar áudio:",
        err
      );

      setRecording(false);
      setRecordingSeconds(0);

      window.alert(
        "Não foi possível enviar o áudio."
      );
    }
  };

  const handleChooseFile = event => {
    const file =
      event.target.files?.[0];

    if (!file) return;

    if (
      file.size >
      20 * 1024 * 1024
    ) {
      window.alert(
        "O arquivo deve ter no máximo 20 MB."
      );

      event.target.value = "";
      return;
    }

    setAttachment(file);
  };

  const downloadAttachment = async item => {
    try {
      const response = await api.get(
        `/chats/${chat.id}/files/${item.id}`,
        {
          responseType: "blob"
        }
      );

      const url =
        window.URL.createObjectURL(
          new Blob([response.data])
        );

      const anchor =
        document.createElement("a");

      anchor.href = url;
      anchor.download =
        item.mediaName || "arquivo";

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(
        "Erro ao baixar arquivo:",
        err
      );
    }
  };

  const fileExtension = name => {
    const parts =
      String(name || "").split(".");

    return parts.length > 1
      ? parts.pop()
      : "arquivo";
  };

  const avatarForMessage = item => {
    const profileImage =
      Number(item.senderId) === Number(user.id)
        ? user.profileImage
        : item?.sender?.profileImage;

    return profileImage
      ? `${backendUrl}/public/avatar/${profileImage}`
      : undefined;
  };

  return (
    <Paper elevation={0} className={classes.mainContainer}>
      <div className={classes.header}>
        <Avatar
          src={headerAvatar}
          className={classes.headerAvatar}
        >
          {participants.length > 2 ? (
            <GroupOutlinedIcon fontSize="small" />
          ) : (
            initials(chatTitle)
          )}
        </Avatar>

        <div className={classes.headerInfo}>
          <Typography className={classes.headerTitle}>
            {chatTitle}
          </Typography>

          <div className={classes.headerSubtitle}>
            {subtitle}
          </div>
        </div>
      </div>

      <div
        ref={listRef}
        onScroll={handleScroll}
        className={classes.messageList}
      >
        {Array.isArray(messages) &&
          messages.map(item => {
            const mine =
              Number(item.senderId) === Number(user.id);

            return (
              <div
                key={item.id}
                data-chat-message-id={item.id}
                className={`${classes.messageRow} ${
                  mine ? classes.messageRowMine : ""
                }`}
              >
                {!mine ? (
                  <Avatar
                    src={avatarForMessage(item)}
                    className={classes.messageAvatar}
                  >
                    {initials(item?.sender?.name)}
                  </Avatar>
                ) : null}

                <div
                  className={`${classes.bubble} ${
                    mine ? classes.bubbleMine : ""
                  }`}
                >
                  <IconButton
                    size="small"
                    className={classes.replyButton}
                    title="Responder"
                    onClick={() => startReply(item)}
                  >
                    <ReplyIcon
                      style={{ fontSize: 15 }}
                    />
                  </IconButton>

                  {!mine ? (
                    <span className={classes.sender}>
                      {item?.sender?.name || "Usuário"}
                    </span>
                  ) : null}

                  {item?.replyToMessage ? (
                    <div
                      className={classes.quotedMessage}
                      title="Ir para mensagem original"
                      onClick={() =>
                        scrollToQuotedMessage(
                          item.replyToMessage.id
                        )
                      }
                    >
                      <span className={classes.quotedAuthor}>
                        {item.replyToMessage?.sender?.name ||
                          "Usuário"}
                      </span>

                      <div className={classes.quotedText}>
                        {messagePreview(
                          item.replyToMessage
                        )}
                      </div>
                    </div>
                  ) : null}

                  {item.message ? (
                    <div className={classes.messageText}>
                      {item.message}
                    </div>
                  ) : null}

                  {item.mediaName &&
                  item.mediaPath ? (
                    isAudioFile(
                      item.mediaName
                    ) ? (
                      <InternalChatAudio
                        chatId={chat.id}
                        item={item}
                        classes={classes}
                      />
                    ) : (
                      <div className={classes.fileCard}>
                        <div className={classes.fileIcon}>
                          <InsertDriveFileOutlinedIcon
                            style={{ fontSize: 19 }}
                          />
                        </div>

                        <div className={classes.fileInfo}>
                          <div className={classes.fileName}>
                            {item.mediaName}
                          </div>

                          <div className={classes.fileType}>
                            {fileExtension(
                              item.mediaName
                            )}
                          </div>
                        </div>

                        <IconButton
                          size="small"
                          className={classes.fileDownload}
                          title="Baixar arquivo"
                          onClick={() =>
                            downloadAttachment(item)
                          }
                        >
                          <GetAppRoundedIcon
                            style={{ fontSize: 18 }}
                          />
                        </IconButton>
                      </div>
                    )
                  ) : null}

                  <span className={classes.time}>
                    {formatDateTime(item.createdAt)}
                  </span>
                </div>

                {mine ? (
                  <Avatar
                    src={avatarForMessage(item)}
                    className={classes.messageAvatar}
                  >
                    {initials(user?.name)}
                  </Avatar>
                ) : null}
              </div>
            );
          })}

        <div ref={baseRef} />
      </div>

      <div className={classes.inputArea}>
        {replyingTo ? (
          <div className={classes.replyPreview}>
            <ReplyIcon
              style={{
                fontSize: 18,
                color: "#0057FF"
              }}
            />

            <div className={classes.replyPreviewContent}>
              <span className={classes.replyPreviewAuthor}>
                Respondendo a{" "}
                {replyingTo?.sender?.name ||
                  (Number(replyingTo?.senderId) ===
                  Number(user.id)
                    ? user?.name
                    : "Usuário")}
              </span>

              <div className={classes.replyPreviewText}>
                {messagePreview(replyingTo)}
              </div>
            </div>

            <IconButton
              size="small"
              title="Cancelar resposta"
              onClick={() => setReplyingTo(null)}
            >
              <CloseRoundedIcon
                style={{ fontSize: 17 }}
              />
            </IconButton>
          </div>
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.webp"
          onChange={handleChooseFile}
        />

        {attachment ? (
          <div className={classes.attachmentPreview}>
            <InsertDriveFileOutlinedIcon
              style={{
                fontSize: 20,
                color: "#0057FF"
              }}
            />

            <span className={classes.attachmentName}>
              {attachment.name}
            </span>

            <IconButton
              size="small"
              onClick={() => {
                setAttachment(null);

                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            >
              <CloseRoundedIcon
                style={{ fontSize: 17 }}
              />
            </IconButton>
          </div>
        ) : null}

        {emojiOpen && !recording ? (
          <div className={classes.emojiPickerWrap}>
            <Picker
              native
              showPreview={false}
              showSkinTones={false}
              emojiSize={22}
              perLine={8}
              title="Emojis"
              onSelect={handleSelectEmoji}
            />
          </div>
        ) : null}

        <div className={classes.composer}>
          {recording ? (
            <div className={classes.recordingComposer}>
              <IconButton
                className={classes.recordingCancel}
                title="Cancelar gravação"
                disabled={loading}
                onClick={handleCancelRecording}
              >
                <CloseRoundedIcon
                  style={{ fontSize: 20 }}
                />
              </IconButton>

              <div className={classes.recordingStatus}>
                <span
                  className={classes.recordingDot}
                />

                <span
                  className={classes.recordingLabel}
                >
                  Gravando áudio
                </span>

                <span
                  className={classes.recordingTimer}
                >
                  {formatRecordingTime(
                    recordingSeconds
                  )}
                </span>
              </div>

              <IconButton
                className={classes.recordingSend}
                title="Enviar áudio"
                disabled={loading}
                onClick={handleSendRecording}
              >
                <SendRoundedIcon
                  style={{ fontSize: 18 }}
                />
              </IconButton>
            </div>
          ) : (
            <>
              <IconButton
                className={classes.attachButton}
                title="Anexar documento"
                disabled={loading}
                onClick={() =>
                  fileInputRef.current?.click()
                }
              >
                <AttachFileRoundedIcon
                  style={{ fontSize: 19 }}
                />
              </IconButton>

              <IconButton
                className={classes.emojiButton}
                title="Abrir emojis"
                disabled={loading}
                onClick={() =>
                  setEmojiOpen(
                    current => !current
                  )
                }
              >
                <MoodRoundedIcon
                  style={{ fontSize: 20 }}
                />
              </IconButton>

              <InputBase
                inputRef={messageInputRef}
                multiline
                placeholder="Digite uma mensagem para a equipe..."
                value={contentMessage}
                className={classes.input}
                onChange={event =>
                  setContentMessage(
                    event.target.value
                  )
                }
                onKeyDown={event => {
                  if (
                    event.key === "Enter" &&
                    !event.shiftKey
                  ) {
                    event.preventDefault();
                    send();
                  }
                }}
              />

              {!contentMessage.trim() &&
              !attachment ? (
                <IconButton
                  className={classes.micButton}
                  title="Gravar áudio"
                  disabled={loading}
                  onClick={handleStartRecording}
                >
                  <MicRoundedIcon
                    style={{ fontSize: 20 }}
                  />
                </IconButton>
              ) : null}

              <IconButton
                className={classes.sendButton}
                disabled={
                  (
                    !contentMessage.trim() &&
                    !attachment
                  ) ||
                  loading
                }
                onClick={send}
              >
                <SendRoundedIcon
                  style={{ fontSize: 18 }}
                />
              </IconButton>
            </>
          )}
        </div>
      </div>
    </Paper>
  );
}
